/**
 * iter225 — Planification / replanification d'un OR dans l'agenda.
 *
 * Cas d'usage : le devis est accepté mais le client ne laisse pas le véhicule
 * tout de suite. On fixe une date : l'OR part dans l'agenda et disparaît du
 * kanban jusqu'au jour du RDV.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarClock, Loader2, Car } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function OrPlanifierRdvDialog({ open, onClose, or, onPlanned }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ date: "", heure_debut: "08:00", duree_min: 60, type_intervention_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  // iter236 — Réservation d'un véhicule de remplacement au moment de la planification
  const [rempl, setRempl] = useState({ enabled: false, flotte_id: "", duree_jours: 1 });
  const [dispo, setDispo] = useState([]);
  const [loadingDispo, setLoadingDispo] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      date: or?.rdv_planifie_date || "",
      heure_debut: or?.rdv_planifie_heure || "08:00",
      duree_min: or?.rdv_planifie_duree_min || 60,
      type_intervention_id: "",
      notes: "",
    });
    setRempl({ enabled: false, flotte_id: "", duree_jours: 1 });
    api.get("/types-intervention", { params: { actif_only: true } })
      .then(({ data }) => {
        setTypes(data || []);
        setForm((f) => ({ ...f, type_intervention_id: f.type_intervention_id || data?.[0]?.id || "" }));
      })
      .catch(() => toast.error("Impossible de charger les types d'intervention"));
  }, [open, or]);

  const periode = () => {
    const start = `${form.date}T${form.heure_debut || "08:00"}:00`;
    const end = new Date(new Date(start).getTime() + (rempl.duree_jours || 1) * 86400000);
    return { date_debut: start, date_fin: end.toISOString().slice(0, 19) };
  };

  // Véhicules de remplacement disponibles sur la période choisie
  useEffect(() => {
    if (!open || !rempl.enabled || !form.date) { setDispo([]); return; }
    setLoadingDispo(true);
    api.get("/reservations/flotte-disponible", { params: periode() })
      .then(({ data }) => {
        const list = data || [];
        setDispo(list);
        setRempl((r) => (r.flotte_id && !list.some((f) => f.id === r.flotte_id) ? { ...r, flotte_id: "" } : r));
      })
      .catch(() => setDispo([]))
      .finally(() => setLoadingDispo(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rempl.enabled, rempl.duree_jours, form.date, form.heure_debut]);

  const submit = async () => {
    if (!form.date) return toast.error("Choisis une date");
    if (rempl.enabled && !rempl.flotte_id) return toast.error("Choisis un véhicule de remplacement");
    setSaving(true);
    try {
      const { data } = await api.post(`/or/${or.id}/planifier-rdv`, form);
      // Réservation du véhicule de remplacement rattachée au RDV créé
      if (rempl.enabled && rempl.flotte_id) {
        const p = periode();
        try {
          await api.post("/reservations", {
            flotte_id: rempl.flotte_id,
            rdv_id: data?.rdv?.id || null,
            client_id: or.client_id,
            date_debut: p.date_debut,
            date_fin: p.date_fin,
            commentaire: `OR ${or.numero}`,
          });
          toast.success("Véhicule de remplacement réservé");
        } catch (e) {
          toast.warning(
            "OR planifié, mais la réservation du véhicule a échoué : "
            + (e.response?.data?.detail || "erreur"),
          );
        }
      }
      toast.success(data.action === "replanifie" ? "OR replanifié dans l'agenda" : "OR planifié dans l'agenda");
      onPlanned?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de planification");
    } finally { setSaving(false); }
  };

  const annuler = async () => {
    setSaving(true);
    try {
      await api.delete(`/or/${or.id}/planifier-rdv`);
      toast.success("Planification annulée — l'OR revient dans le kanban");
      onPlanned?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="or-planifier-dialog"
        /* iter236 — Un clic à côté (sortie du calendrier natif, du menu heure…) ne
           doit PLUS fermer la fenêtre : seuls la croix, « Fermer » ou Échap ferment. */
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#52a675]" />
            {or?.rdv_planifie_date ? "Replanifier dans l'agenda" : "Planifier dans l'agenda"}
          </DialogTitle>
          <DialogDescription>
            L&apos;OR est placé au calendrier et disparaît du kanban atelier jusqu&apos;au jour du rendez-vous.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="or-planifier-date"
              />
            </div>
            <div>
              <Label className="text-xs">Heure</Label>
              <Input
                type="time"
                value={form.heure_debut}
                onChange={(e) => setForm((f) => ({ ...f, heure_debut: e.target.value }))}
                data-testid="or-planifier-heure"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Durée (minutes)</Label>
              <Input
                type="number"
                min={15}
                step={15}
                value={form.duree_min}
                onChange={(e) => setForm((f) => ({ ...f, duree_min: e.target.value }))}
                data-testid="or-planifier-duree"
              />
            </div>
            <div>
              <Label className="text-xs">Type d&apos;intervention</Label>
              <Select
                value={form.type_intervention_id}
                onValueChange={(v) => setForm((f) => ({ ...f, type_intervention_id: v }))}
              >
                <SelectTrigger data-testid="or-planifier-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Note pour l&apos;agenda (optionnel)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Le client rapporte le véhicule le matin…"
              data-testid="or-planifier-notes"
            />
          </div>

          {/* iter236 — Véhicule de remplacement réservé en même temps que le RDV */}
          <div className="border-t border-gray-100 pt-3" data-testid="or-planifier-remplacement">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={rempl.enabled}
                onChange={(e) => setRempl((r) => ({ ...r, enabled: e.target.checked }))}
                className="accent-[#52a675]"
                data-testid="or-planifier-rempl-toggle"
              />
              <Car className="w-4 h-4 text-[#52a675]" /> Véhicule de remplacement
            </label>
            {rempl.enabled && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">Durée du prêt (jours)</Label>
                  <Input
                    type="number" min={1} max={30}
                    value={rempl.duree_jours}
                    onChange={(e) => setRempl((r) => ({ ...r, duree_jours: parseInt(e.target.value, 10) || 1 }))}
                    data-testid="or-planifier-rempl-jours"
                  />
                </div>
                <div>
                  <Label className="text-xs">Véhicule disponible</Label>
                  <Select
                    value={rempl.flotte_id}
                    onValueChange={(v) => setRempl((r) => ({ ...r, flotte_id: v }))}
                  >
                    <SelectTrigger data-testid="or-planifier-rempl-vehicule">
                      <SelectValue placeholder={loadingDispo ? "Recherche…" : "Choisir…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {dispo.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.plaque} · {f.marque} {f.modele}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!loadingDispo && rempl.enabled && dispo.length === 0 && (
                    <p className="text-[11px] text-orange-700 mt-1">
                      Aucun véhicule libre sur cette période.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {or?.rdv_planifie_date ? (
            <Button variant="outline" onClick={annuler} disabled={saving} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="or-planifier-annuler">
              Annuler la planification
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Fermer</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="or-planifier-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
